import { apiClient } from "@vv/api";
import type {
  AssignPayload,
  Attachment,
  Comment,
  CompletePayload,
  CreateRequestPayload,
  RejectPayload,
  RequestChangesPayload,
  ValidationRequest,
} from "../domain/request.types";

export interface RequestListFilters {
  teamId?: string;
  status?: string;
  priority?: string;
  assigneeId?: string;
}

function toQuery(filters: RequestListFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const requestsApi = {
  list: (filters: RequestListFilters = {}) =>
    apiClient.get<ValidationRequest[]>(`/requests${toQuery(filters)}`),

  get: (id: string) => apiClient.get<ValidationRequest>(`/requests/${id}`),

  /** Wizard completion - creates the request already `Submitted`, per the approved spec. */
  create: (payload: CreateRequestPayload) =>
    apiClient.post<ValidationRequest>("/requests", payload),

  /** Accepts and assigns in one call - see AssignPayload for what that requires. */
  accept: (id: string, payload: AssignPayload) =>
    apiClient.post<ValidationRequest>(`/requests/${id}/accept`, payload),

  reject: (id: string, payload: RejectPayload) =>
    apiClient.post<ValidationRequest>(`/requests/${id}/reject`, payload),

  start: (id: string) => apiClient.post<ValidationRequest>(`/requests/${id}/start`),

  complete: (id: string, payload: CompletePayload) =>
    apiClient.post<ValidationRequest>(`/requests/${id}/complete`, payload),

  /** Files a rework claim - stays `Completed` pending the Manager's approveRework/rejectRework decision. */
  requestRework: (id: string, payload: RequestChangesPayload) =>
    apiClient.post<ValidationRequest>(`/requests/${id}/request-rework`, payload),

  /** Approves a pending rework claim, re-staffing it like a fresh accept. */
  approveRework: (id: string, payload: AssignPayload) =>
    apiClient.post<ValidationRequest>(`/requests/${id}/approve-rework`, payload),

  /** Denies a pending rework claim - the completed result stands. */
  rejectRework: (id: string) => apiClient.post<ValidationRequest>(`/requests/${id}/reject-rework`),

  listComments: (id: string) => apiClient.get<Comment[]>(`/requests/${id}/comments`),

  addComment: (id: string, body: string) =>
    apiClient.post<Comment>(`/requests/${id}/comments`, { body }),

  listAttachments: (id: string) => apiClient.get<Attachment[]>(`/requests/${id}/attachments`),

  uploadAttachment: (id: string, file: File, category: Attachment["category"]) => {
    const formData = new FormData();
    formData.set("category", category);
    formData.set("file", file);
    return apiClient.post<Attachment>(`/requests/${id}/attachments`, formData);
  },

  getAttachmentDownloadUrl: (requestId: string, attachmentId: string) =>
    apiClient.get<{ url: string }>(`/requests/${requestId}/attachments/${attachmentId}/download`),
};
